import React, { createContext, useEffect, useState } from "react";

const CART_STORAGE_KEY = "Armelvari:cart";
const LEGACY_CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "Armelvari:wishlist";

const getStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readArray = (key, fallbackKey) => {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const storedValue =
      storage.getItem(key) ||
      (fallbackKey ? storage.getItem(fallbackKey) : null);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be disabled or full; the in-memory shopping flow still works.
  }
};

const getItemId = (itemOrId) =>
  itemOrId && typeof itemOrId === "object" ? itemOrId.id : itemOrId;

const hasSameId = (left, right) => String(left) === String(right);

const isSameCartLine = (item, id, size, color) =>
  hasSameId(item.id, id) && item.size === size && item.color === color;

export const CartContext = createContext({
  cartItems: [],
  wishlistItems: [],
  cartCount: 0,
  wishlistCount: 0,
  addToCart: () => {},
  addCart: () => {},
  removeFromCart: () => {},
  removeCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addToWishlist: () => {},
  addWishlist: () => {},
  removeFromWishlist: () => {},
  removeWishlist: () => {},
  toggleWishlist: () => {},
  isWishlist: () => false,
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readArray(CART_STORAGE_KEY, LEGACY_CART_STORAGE_KEY)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readArray(WISHLIST_STORAGE_KEY)
  );

  useEffect(() => {
    persistArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = (productOrId, quantity = 1, size = "", color = "") => {
    const productId = getItemId(productOrId);
    if (productId === undefined || productId === null) return;

    const parsedQuantity = Number.parseInt(quantity, 10);
    const safeQuantity = Number.isFinite(parsedQuantity)
      ? Math.max(1, parsedQuantity)
      : 1;

    setCartItems((currentItems) => {
      const existingItem = currentItems.find((item) =>
        isSameCartLine(item, productId, size, color)
      );

      if (!existingItem) {
        return [
          ...currentItems,
          { id: productId, size, color, quantity: safeQuantity },
        ];
      }

      return currentItems.map((item) =>
        isSameCartLine(item, productId, size, color)
          ? { ...item, quantity: item.quantity + safeQuantity }
          : item
      );
    });
  };

  const removeFromCart = (productOrId, size = "", color = "") => {
    const productId = getItemId(productOrId);
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) => !isSameCartLine(item, productId, size, color)
      )
    );
  };

  const updateQuantity = (productOrId, size = "", color = "", quantity = 1) => {
    const productId = getItemId(productOrId);
    const parsedQuantity = Number.parseInt(quantity, 10);
    const safeQuantity = Number.isFinite(parsedQuantity)
      ? Math.max(1, parsedQuantity)
      : 1;

    setCartItems((currentItems) =>
      currentItems.map((item) =>
        isSameCartLine(item, productId, size, color)
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const addToWishlist = (productOrId) => {
    const productId = getItemId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((currentItems) =>
      currentItems.some((id) => hasSameId(id, productId))
        ? currentItems
        : [...currentItems, productId]
    );
  };

  const removeFromWishlist = (productOrId) => {
    const productId = getItemId(productOrId);
    setWishlistItems((currentItems) =>
      currentItems.filter((id) => !hasSameId(id, productId))
    );
  };

  const toggleWishlist = (productOrId) => {
    const productId = getItemId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((currentItems) =>
      currentItems.some((id) => hasSameId(id, productId))
        ? currentItems.filter((id) => !hasSameId(id, productId))
        : [...currentItems, productId]
    );
  };

  const isWishlist = (productOrId) => {
    const productId = getItemId(productOrId);
    return wishlistItems.some((id) => hasSameId(id, productId));
  };

  const cartCount = cartItems.reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0
  );
  const wishlistCount = wishlistItems.length;

  const contextValue = {
    cartItems,
    cart: cartItems,
    cartCount,
    addToCart,
    addCart: addToCart,
    removeFromCart,
    removeCart: removeFromCart,
    updateQuantity,
    clearCart,
    wishlistItems,
    wishlist: wishlistItems,
    wishlistCount,
    addToWishlist,
    addWishlist: addToWishlist,
    removeFromWishlist,
    removeWishlist: removeFromWishlist,
    toggleWishlist,
    isWishlist,
    isInWishlist: isWishlist,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
